import { FormControl, FormGroup } from '@angular/forms';

import { map } from 'rxjs/operators';

import {
    ServerResponse,
    ServerResponseData,
    HttpStatusCodesEnum,
    PaymentsService,
    PaymentReportModel,
    PaymentModel,
    PaymentProcessesEnum,
    UpdateAdjustmentInputModel
} from '@spa/api';

import { InvalidAmountKey } from '@spa/validators';

import { Helper } from '../../../shared/classes';

import { PaymentsFormErrorStatesEnum } from '../enums/error-states';

import { PaymentsFormControls, PaymentsFormControlsInterface } from '../../../../assets/descriptors';
import { MaximumAmountValidatorKey } from 'appEntries/panel/validators';

import * as moment from 'moment';
import { environment } from 'appEnv/environment';
import { EventsService } from '../services';

export class PaymentsForm {
    private readonly paymentsService: PaymentsService;

    public form: FormGroup;
    public controls: PaymentsFormControlsInterface;

    constructor(private readonly eventsService: EventsService, paymentsService: PaymentsService) {
        this.paymentsService = paymentsService;
    }

    private createAdjustmentRequest(paymentDetailed: PaymentReportModel): ServerResponse<boolean> {
        const amount = !this.form.value[this.controls.amount.name] ? null :
            parseFloat(Helper.compactString(this.form.value[this.controls.amount.name]));
        const category = this.form.value[this.controls.category.name];
        const comment = this.form.value[this.controls.comment.name];
        const date = this.form.value[this.controls.createDate.name];

        const model = new UpdateAdjustmentInputModel(
            paymentDetailed.paymentReference,
            amount,
            date,
            category,
            comment);

        return this.paymentsService.updateAdjustment(model).pipe(
            map((response: ServerResponseData<null>) => {
                if (response.status !== HttpStatusCodesEnum.Ok) {
                    return Helper.errorsHelper().createErrorResponse(
                        response,
                        PaymentsFormErrorStatesEnum.UpdateAdjustmentFailed
                    );
                }

                paymentDetailed.amount = amount.toString();
                paymentDetailed.category = category;
                paymentDetailed.description = comment;
                paymentDetailed.createDate = moment(date).format(environment.dateFormat);

                this.eventsService.forcePaymentGet.next(true);

                return Helper.createSuccessResponse<boolean>(true);
            })
        );
    }

    initForm(payment: PaymentReportModel): void {
        this.form = new FormGroup({});

        this.controls = PaymentsFormControls(payment);

        this.controls.amount.value = !payment ? '0.00' : parseFloat(payment.amount).toString();

        Object.keys(this.controls).forEach(key => {
            const control = this.controls[key];

            if (control) {
                this.form.addControl(control.name, new FormControl(control.value, control.validators));
            }
        });
    }

    validateForm(messageFunction: (controlLabel?: string, controlLength?: number) => { [key: string]: string; }, maxAmount?: number): boolean {
        if (this.form.invalid) {
            Object.keys(this.controls).forEach((controlKey) => {
                const control = this.controls[controlKey];
                const formControl = this.form.controls[control.name];

                if (formControl && formControl.invalid) {
                    control.errors = Object.keys(formControl.errors).map((errorKey: string) => {
                        return messageFunction(control.placeholder)[errorKey];
                    });
                }
            });

            return false;
        }

        const amountControl = this.form.controls[this.controls.amount.name];

        if (amountControl && (amountControl.value !== null)) {
            const fixedAmount = parseFloat(Helper.compactString(amountControl.value));

            if (fixedAmount === 0) {
                this.controls.amount.setError(messageFunction(this.controls.amount.placeholder)[InvalidAmountKey]);

                return false;
            }
            if (maxAmount && fixedAmount > maxAmount) {
                this.controls.amount.setError(messageFunction(this.controls.amount.placeholder, maxAmount)[MaximumAmountValidatorKey]);

                return false;
            }
        }

        return true;
    }

    createRequest(paymentDetailed: PaymentReportModel): ServerResponse<boolean> {
        if (paymentDetailed.paymentProcess === PaymentProcessesEnum.Adjustment) {
            return this.createAdjustmentRequest(paymentDetailed);
        }

        const currentAmount = parseFloat(paymentDetailed.amount);
        const newAmount = !this.form.value[this.controls.amount.name] ? null :
            parseFloat(Helper.compactString(this.form.value[this.controls.amount.name]));

        if (newAmount && currentAmount !== newAmount) {
            const payment = new PaymentModel();

            payment.paymentReference = paymentDetailed.paymentReference;
            payment.paymentProcess = paymentDetailed.paymentProcess;
            payment.amount = newAmount;

            return this.paymentsService.updatePayment(payment).pipe(
                map((response: ServerResponseData<null>) => {
                    if (response.status !== HttpStatusCodesEnum.Ok) {
                        return Helper.errorsHelper().createErrorResponse(
                            response,
                            PaymentsFormErrorStatesEnum.UpdatePaymentFailed
                        );
                    }

                    paymentDetailed.amount = payment.amount.toString();

                    return Helper.createSuccessResponse<boolean>(true);
                })
            );
        }

        return Helper.createSuccessResponseObservable<boolean>(true);
    }
}
